import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, message, Modal, Row, Space, Spin, Tooltip } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import CompanyTicketClassInput from '@/components/FormItems/CompanyTicketClassInput';
import CreateSegmentModal from './CreateSegmentModal';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import { exportService } from '@/services/exportService';
import ExportBtn from '@/components/app/ExportBtn';
import SegmentTariffParametersModal from './SegmentTariffParameters';
import ImportBtn from '@/components/app/ImportBtn';
import ImportFileModal from '@/components/modals/ImportFileModal';
import { RcFile } from 'antd/lib/upload';
import i18n from '@/helpers/i18n';
import { CompanyTicketClassDto, BaseImportColumn, BaseImportResponse, BasicTariffDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageFleetTrip, useCanManageTicketTariff } from '@/hooks/useHasPermissions';

const PAGE_TITLE = 'ticketTariffsPage_Title';

const columns: IColumnType<CompanyTicketClassDto>[] = [
  {
    title: 'baggageTariffsPage_From',
    dataIndex: 'originStationName',
    ellipsis: true,
  },
  {
    title: 'baggageTariffsPage_To',
    dataIndex: 'destinationStationName',
    ellipsis: true,
  },

  {
    title: 'ticketTariffsPage_BaseRate',
    dataIndex: 'amount',
    ellipsis: true,
  },
  {
    title: 'ticketTariffsPage_BaseRateReturn',
    dataIndex: 'returnAmount',
    ellipsis: true,
  },

  {
    title: 'baggageTariffsPage_TicketClass',
    dataIndex: 'ticketClassName',
    ellipsis: true,
  },
  {
    title: 'ticketTariffsPage_Distance',
    dataIndex: 'distance',
    ellipsis: true,
  },
];

const filters: Array<IFilterItem> = [
  {
    type: 'SELECT',
    name: 'CompanyTicketClassId',
    label: 'baggageTariffsPage_TicketClass',
    customElm: <CompanyTicketClassInput placeholder={i18n.t('baggageTariffsPage_TicketClass')} />,
  },
  { type: 'TEXT', name: 'searchText', label: 'baggageTariffsPage_SearchText' },
];

const TicketTariffsPage = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [segmentForm] = Form.useForm();

  const [formVisible, setFormVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displaySegmentModal, setDisplaySegmentModal] = React.useState<boolean>(false);
  const [tariffId, setTariffId] = React.useState<string>();
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [importCols, setImportCols] = React.useState<BaseImportColumn[]>([]);
  const [displayImportModal, setDisplayImportModal] = React.useState<boolean>(false);
  const canUpdate = useCanManageTicketTariff();

  React.useEffect(() => {
    apiSdk.BasicTariffService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);

  const updateEntity = values => {
    setLoading(true);
    apiSdk.BasicTariffService.updateBasicTariff({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onExportExcel = (cols: string[]) => {
    exportService
      .exportTicketTariff({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportTicketTariff({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toPdf();
  };
  const onDelete = (segmentId: string) => {
    apiSdk.BasicTariffService.delete({
      segmentId: segmentId,
    })
      .then(response => {
        if (response?.success) {
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleImportClick = () => {
    if (!importCols.length) {
      apiSdk.BasicTariffService.getImportColumns().then(res => {
        setImportCols(res);
        setDisplayImportModal(true);
      });
    } else {
      setDisplayImportModal(true);
    }
  };
  const onImport = (file: RcFile): Promise<BaseImportResponse> => {
    return apiSdk.BasicTariffService.import({
      file: file,
      fileName: file.name,
    });
  };
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                className="nta-create-btn"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  segmentForm.resetFields();
                  setDisplaySegmentModal(true);
                }}
              >
                {t('baggageTariffsPage_CreateNewSegment')}
              </Button>,
            ]
          : null
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<BasicTariffDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/basic-tariffs/get-basic-tariffs"
          bordered
          order_by="createdOn"
          order_by_asc={false}
          allowEdit={canUpdate}
          searchBtnAlignLeft
          actionColumnWidth={150}
          onEdit={record => {
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
            });
          }}
          allowDelete={canUpdate}
          onDelete={record => {
            onDelete(record.segmentId || '');
          }}
          extraBtns={
            <Space>
              <ExportBtn
                disabled={!exportColumns?.length}
                onClick={() => {
                  toggleExportModal(true);
                }}
              />
              {canUpdate ? <ImportBtn onClick={handleImportClick} /> : null}
            </Space>
          }
          filters={filters}
          extraActions={[
            {
              render: record => {
                return (
                  <Tooltip title={t('ticketTariffsPage_Segment_Tariff')}>
                    <Button size="small" onClick={() => setTariffId(record.id)} type="primary" danger icon={<PlusCircleOutlined />} />
                  </Tooltip>
                );
              },
            },
          ]}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            className="nta-custom-form"
            title={t('baggageTariffsPage_UpdateTicketTariffs')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  updateEntity(values);
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Row gutter={ROW_GUTTER}>
                  <Col md={24}>
                    <Form.Item label={t('ticketTariffsPage_BaseRate')} name="amount" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item
                      label={t('ticketTariffsPage_BaseRateReturn')}
                      name="returnAmount"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item
                      label={t('baggageTariffsPage_TicketClass')}
                      name="baseTicketClassId"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <CompanyTicketClassInput />
                    </Form.Item>
                    <Form.Item label={t('ticketTariffsPage_Distance')} name="distance" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
      {displaySegmentModal ? (
        <CreateSegmentModal
          onClose={reload => {
            setDisplaySegmentModal(false);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
      {showExportModal ? (
        <ExportColumnsSelectorModal
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
      {tariffId ? (
        <SegmentTariffParametersModal
          onClose={() => {
            setTariffId(undefined);
          }}
          tariffId={tariffId}
        />
      ) : null}
      {displayImportModal ? (
        <ImportFileModal
          sampleDownloadUrl="/ExcelTemplates/ImportExcelFormatBasicTarrif.xlsx"
          cols={importCols}
          onImport={onImport}
          onClose={reload => {
            setDisplayImportModal(false);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
    </MainContent>
  );
};
export default TicketTariffsPage;
